import { FC } from 'react';
import { Grid, Header, Icon, SemanticICONS, SemanticWIDTHS } from 'semantic-ui-react';

import { useDesignConfig } from '../../../config/design.context';
import { bem } from '../../../core/design/bem';
import BlockContainer from '../../BlockContainer';
import { ContainerProps } from '../types';
import './InfoBlock.scss';

const css = bem('InfoBlock');

type IconShape = 'normal' | 'square' | 'diamond' | 'circle' | 'bubble' | 'hexagonal';
type Block = {
  _id: string;
  title: string;
  icon?: SemanticICONS;
  description?: string;
  style?: Record<string, any>;
};

type InfoBlockProps = {
  _id: string;
  container?: ContainerProps;
  className?: string;
  blocks?: Block[];
  itemsPerRow?: SemanticWIDTHS;
  iconShape?: IconShape;
  design?: {
    color?: string;
  } & Record<string, any>;
};

function generateIconStyles({
  shape,
  color: globalColor,
  itemColor,
}: {
  shape: IconShape;
  color: string;
  itemColor?: string;
}): { wrapperStyles?: Record<string, any>; iconStyles?: Record<string, any> } {
  const color = itemColor || globalColor;
  switch (shape) {
    case 'square':
      return {
        wrapperStyles: { backgroundColor: color },
        iconStyles: { color: 'white' },
      };
    case 'diamond':
      return {
        wrapperStyles: { '--border-color': color },
        iconStyles: { color },
      };
    case 'circle':
      return {
        wrapperStyles: { backgroundColor: color, borderColor: color },
        iconStyles: { color: 'white' },
      };
    case 'bubble':
      return {
        wrapperStyles: { borderColor: color },
        iconStyles: { color },
      };
    case 'hexagonal':
      return {
        wrapperStyles: {
          '--icon-color': color,
          'backgroundColor': color,
        },
        iconStyles: { color: 'white' },
      };
    default:
      return {
        iconStyles: { color },
      };
  }
}

export const InfoBlock: FC<InfoBlockProps> = (props) => {
  const { primaryColor } = useDesignConfig();
  const {
    _id,
    container,
    className,
    blocks,
    itemsPerRow = 4,
    iconShape = 'normal',
    design = {},
  } = props;
  const { color: iconColor = primaryColor } = design;
  if (!blocks?.length) return null;
  return (
    <BlockContainer {...container} className={css({ _id }).mix(className).toString()}>
      <Grid stretched stackable columns={itemsPerRow}>
        {blocks.map((block) => {
          const { _id: blockId, title, description, style, icon } = block;
          const { color: itemColor } = style || {};
          const { wrapperStyles, iconStyles } = generateIconStyles({
            shape: iconShape,
            color: iconColor,
            itemColor,
          });
          return (
            <Grid.Column key={blockId} className={css('item', { iconShape }).toString()}>
              {icon && (
                <div className="icon-wrapper" style={wrapperStyles}>
                  <Icon name={icon as SemanticICONS} className="icon" style={iconStyles} />
                </div>
              )}
              <Header as="h3" className="title" style={{ color: itemColor || iconColor }}>
                {title}
              </Header>
              {description && (
                <div className="description" dangerouslySetInnerHTML={{ __html: description }} />
              )}
            </Grid.Column>
          );
        })}
      </Grid>
    </BlockContainer>
  );
};
